import type { QueryClient } from '@tanstack/react-query';
import {
  ProspectBookSort,
  type CallSheetMembershipDto,
  type MatchCandidateLocator,
  type PossibleMatchesDto,
  type ProspectBookDto,
  type ProspectBookQuery,
  type ProspectBookRowDto,
} from '@signalvote/shared';
import { candidateKey } from '../../lib/fundraising/matchVerdict';
import { bookQueryOfKey, queryKeys } from '../../lib/queryKeys';

/**
 * Keeping the cached Prospects books honest after a small change without reading them again. A book read is the
 * whole campaign — every Prospect, their matches and their calls — so a change patches the rows and call books it
 * touched from what the server returned and re-reads only the small reads around the book (a person's page, the
 * summary behind the tab counts, the call books). Only a change the patch can't express re-reads a book: a call
 * book's people or order change what a book narrowed to it, or sorted by call books, shows and in what order.
 */

/** Whether a cached read under the Prospects prefix is a book (a plain list, a page or a summary isn't). */
export const isBook = (data: unknown): data is ProspectBookDto =>
  !!data && typeof data === 'object' && 'rows' in data && Array.isArray((data as ProspectBookDto).rows);

/**
 * Change every cached book; any other read under the prefix is left as it is. A patch never makes a book look
 * freshly read — it keeps the time it was read and whether it was marked stale — so a book that needed reading
 * still reads on its next open.
 */
function patchBooks(queryClient: QueryClient, change: (book: ProspectBookDto) => ProspectBookDto) {
  for (const query of queryClient.getQueryCache().findAll({ queryKey: queryKeys.fundraising.prospectBooks() })) {
    const data: unknown = query.state.data;
    if (!isBook(data)) continue;
    const next = change(data);
    if (next === data) continue;
    const { dataUpdatedAt, isInvalidated } = query.state;
    queryClient.setQueryData(query.queryKey, next, { updatedAt: dataUpdatedAt });
    if (isInvalidated) query.invalidate();
  }
}

/**
 * Put each person's row, as the server just read it, in place of theirs in every cached book. A row carrying an
 * older token than the cached one is a read that raced a later save, and never replaces it. Nobody is added: a
 * book's rows are the server's to choose.
 */
export function patchBookRows(queryClient: QueryClient, rows: ProspectBookRowDto[]) {
  const fresh = new Map(rows.map((row) => [row.prospect.id, row]));
  if (fresh.size === 0) return;
  patchBooks(queryClient, (book) => {
    let changed = false;
    const patched = book.rows.map((row) => {
      const next = fresh.get(row.prospect.id);
      if (!next || next.prospect.concurrencyVersion < row.prospect.concurrencyVersion) return row;
      changed = true;
      return next;
    });
    return changed ? { ...book, rows: patched } : book;
  });
  // A new row can move a book's chip counts or leave it outside its filter — the server's to say on its next read.
  void markBooksStale(queryClient);
}

/** Change every cached book's call books — who's on each, their names, sizes and tokens. */
export function patchBookSheets(queryClient: QueryClient, change: (sheets: CallSheetMembershipDto[]) => CallSheetMembershipDto[]) {
  patchBooks(queryClient, (book) => ({ ...book, sheets: change(book.sheets) }));
}

/** The call books in the order the server lists them: by name ignoring case (its upper-cased compare), then id. */
export const byName = (sheets: CallSheetMembershipDto[]) =>
  [...sheets].sort((a, b) => {
    const [x, y] = [a.name.toUpperCase(), b.name.toUpperCase()];
    return x < y ? -1 : x > y ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

/**
 * Re-read what sits around the book under the Prospects prefix — a person's page, a plain Prospect list, a
 * "suggested ask becomes" preview and, unless `summaries` is false, the summary behind the tab counts, and unless
 * `possibleMatches` is false, Match review's list — never a book itself.
 */
export function invalidateAroundBooks(
  queryClient: QueryClient,
  { summaries = true, possibleMatches = true }: { summaries?: boolean; possibleMatches?: boolean } = {},
) {
  return queryClient.invalidateQueries({
    queryKey: queryKeys.fundraising.prospects(),
    predicate: ({ queryKey }) =>
      queryKey[3] !== 'book' &&
      (summaries || queryKey[3] !== 'summary') &&
      (possibleMatches || queryKey[3] !== 'possible-matches'),
  });
}

/**
 * Take an answered record off the person in Match review's cached list — and the person off it, one fewer in all,
 * once nothing of theirs waits — from the row the answer returned. False when the answer leaves them a count the
 * patch can't account for (a re-run found them another record meanwhile), which only a re-read can show.
 */
export function patchPossibleMatches(
  queryClient: QueryClient,
  prospectId: string,
  answered: MatchCandidateLocator,
  row: ProspectBookRowDto | null,
): boolean {
  const key = queryKeys.fundraising.possibleMatches();
  const review = queryClient.getQueryData<PossibleMatchesDto>(key);
  if (!review) return true;
  if (!row) return false;
  const person = review.people.find((p) => p.prospect.id === prospectId);
  if (!person) return row.possibleMatchCount === 0;
  const left = person.possibleMatches.filter((c) => candidateKey(c) !== candidateKey(answered));
  if (left.length !== row.possibleMatchCount) return false;
  queryClient.setQueryData<PossibleMatchesDto>(key, {
    people:
      left.length === 0
        ? review.people.filter((p) => p !== person)
        : review.people.map((p) => (p === person ? { prospect: row.prospect, possibleMatches: left } : p)),
    total: left.length === 0 ? Math.max(0, review.total - 1) : review.total,
  });
  return true;
}

/**
 * Mark every book stale without reading any again. A patched row can move a book's chip counts or leave it
 * outside the book's filter, which only the server can say; the next time a book is opened it reads afresh, while
 * the one on screen keeps the patched rows.
 */
export function markBooksStale(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: queryKeys.fundraising.prospectBooks(), refetchType: 'none' });
}

/** Re-read the books whose filters say they show a change the patch can't express. */
export function refetchBooksWhere(queryClient: QueryClient, shows: (query: Required<ProspectBookQuery>) => boolean) {
  return queryClient.invalidateQueries({
    queryKey: queryKeys.fundraising.prospectBooks(),
    predicate: ({ queryKey }) => {
      const query = bookQueryOfKey(queryKey);
      return !!query && shows(query);
    },
  });
}

/** Whether a book shows a call book's people or order: narrowed to it, or sorted by how many books people are on. */
export const showsCallBook = (sheetId: string) => (query: Required<ProspectBookQuery>) =>
  query.sheetId === sheetId || query.sort === ProspectBookSort.CallBooks;
